// Archivo: ui/ExitForm.js

import React, { useState, useMemo, useEffect } from 'react';
import { Combobox } from '@headlessui/react';

// --- CAMBIO: Se añade 'initialData' para la funcionalidad de editar ---
export default function ExitForm({ stock, currentUser, onSave, onCancel, initialData = null }) {
    const [selectedStockId, setSelectedStockId] = useState('');
    const [quantity, setQuantity] = useState('');
    const [reason, setReason] = useState('');
    const [error, setError] = useState('');
    const [stockQuery, setStockQuery] = useState('');

    // --- CAMBIO: Se añade un useEffect para rellenar el formulario al editar ---
    useEffect(() => {
        if (initialData) {
            setSelectedStockId(initialData.stockItem.id);
            setQuantity(initialData.quantity);
            setReason(initialData.reason);
        } else {
            setSelectedStockId('');
            setQuantity('');
            setReason('');
            setError('');
        }
    }, [initialData]);

    const selectedItem = useMemo(() => {
        // Buscamos en el stock general para obtener la cantidad máxima disponible
        return stock.find(item => item.id === selectedStockId);
    }, [selectedStockId, stock]);

    const availableStockForUser = useMemo(() => {
        return stock.filter(item => {
            const hasStock = item.quantity > 0;
            if (!hasStock) return false;
            const isAdminOrSupervisor = currentUser.role === 'administrador' || currentUser.role === 'supervisor';
            if (isAdminOrSupervisor) return true;
            const isInUserLocation = item.locationId === currentUser.location;
            return isInUserLocation;
        });
    }, [stock, currentUser]);

    const filteredStock =
        stockQuery === ''
            ? availableStockForUser
            : availableStockForUser.filter((item) => {
                const designText = item.designName === 'Sin Diseño' ? 'Liso' : item.designName;
                const searchStr = `${item.productName} ${item.size} ${item.color} ${designText} ${item.locationName}`.toLowerCase().replace(/\s+/g, '');
                return searchStr.includes(stockQuery.toLowerCase().replace(/\s+/g, ''));
            });

    const handleSubmit = (e) => {
        e.preventDefault();
        setError('');

        if (!selectedItem || !quantity || !reason.trim()) {
            setError("Debe seleccionar un producto, ingresar una cantidad y una razón.");
            return;
        }

        const numericQuantity = parseInt(quantity, 10);
        if (isNaN(numericQuantity) || numericQuantity <= 0 || numericQuantity > selectedItem.quantity) {
            setError(`La cantidad debe ser un número entre 1 y ${selectedItem.quantity}.`);
            return;
        }
        
        onSave({
            stockItem: selectedItem,
            quantity: numericQuantity,
            reason: reason,
        });

        if(!initialData) {
            setSelectedStockId('');
            setQuantity('');
            setReason('');
            setError('');
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div>
                <Combobox
                    value={selectedStockId}
                    onChange={(stockId) => {
                        setSelectedStockId(stockId);
                        setQuantity('');
                        setError('');
                    }}
                    // --- CAMBIO: Se deshabilita el selector de producto al editar ---
                    disabled={!!initialData}
                >
                    <Combobox.Label className="block text-sm font-medium text-gray-700">Producto en Stock</Combobox.Label>
                    <div className="relative mt-1">
                        <Combobox.Input
                            className="w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 shadow-sm focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500 sm:text-sm disabled:bg-gray-200"
                            onChange={(event) => setStockQuery(event.target.value)}
                            displayValue={(stockId) => {
                                if (!stockId) return '';
                                // Usamos el stock general para mostrar el nombre, ya que el disponible podría estar filtrado
                                const item = stock.find(i => i.id === stockId);
                                return item ? `${item.productName} (${item.size}, ${item.color}) - ${item.designName === 'Sin Diseño' ? 'Liso' : item.designName} | Bodega: ${item.locationName} (Stock: ${item.quantity})` : '';
                            }}
                            placeholder="-- Busca un producto de tu bodega --"
                            onClick={(event) => event.target.select()}
                        />
                        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 3a.75.75 0 01.53.22l3.5 3.5a.75.75 0 01-1.06 1.06L10 4.81 7.03 7.78a.75.75 0 01-1.06-1.06l3.5-3.5A.75.75 0 0110 3zm-3.78 9.28a.75.75 0 011.06 0L10 15.19l2.97-2.91a.75.75 0 111.06 1.06l-3.5 3.5a.75.75 0 01-1.06 0l-3.5-3.5a.75.75 0 010-1.06z" clipRule="evenodd" /></svg>
                        </Combobox.Button>
                        <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                            {filteredStock.map((item) => (
                                <Combobox.Option key={item.id} value={item.id} className={({ active }) => `relative cursor-pointer select-none py-2 pl-3 pr-9 ${ active ? 'bg-cyan-600 text-white' : 'text-gray-900' }`}>
                                    {({ selected }) => (
                                        <span className={`block ${ selected ? 'font-semibold' : '' }`}>
                                            {`${item.productName} (${item.size}, ${item.color}) - ${item.designName === 'Sin Diseño' ? 'Liso' : item.designName} | Bodega: ${item.locationName} (Stock: ${item.quantity})`}
                                        </span>
                                    )}
                                </Combobox.Option>
                            ))}
                        </Combobox.Options>
                    </div>
                </Combobox>
            </div>

            {selectedItem && (
                <div className="p-2 bg-blue-50 border border-blue-200 rounded-md text-sm">
                    <span className="font-semibold">Stock Disponible:</span> {selectedItem.quantity} unidades.
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label htmlFor="quantity" className="block text-sm font-medium text-gray-700">Cantidad a retirar</label>
                    <input type="number" id="quantity" value={quantity} onChange={(e) => setQuantity(e.target.value)}
                        disabled={!selectedItem} max={selectedItem?.quantity} min="1"
                        className="mt-1 block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm disabled:bg-gray-100" />
                </div>
                <div>
                    <label htmlFor="reason" className="block text-sm font-medium text-gray-700">Razón de la Salida (Venta, Muestra, etc.)</label>
                    <input type="text" id="reason" value={reason} onChange={(e) => setReason(e.target.value)}
                        disabled={!selectedItem}
                        className="mt-1 block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm disabled:bg-gray-100" />
                </div>
            </div>

            {error && (<div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">{error}</div>)}

            <div className="flex justify-end gap-4 pt-4">
                <button type="button" onClick={onCancel} className="btn-secondary">Cerrar</button>
                {/* --- CAMBIO: El texto del botón es dinámico --- */}
                <button type="submit" className="btn-primary">
                    {initialData ? 'Actualizar Salida' : 'Añadir al Lote'}
                </button>
            </div>
        </form>
    );
}